import React from 'react';
import { usePermissions } from '../../context/PermissionsContext';
import AccessDenied from '../AccessDenied/AccessDenied';
import LoadingScreen from '../LoadingScreen/LoadingScreen';

/**
 * Route-level permission guard (spec §2.1 / §3.2).
 *
 * Renders its children only when the user is allowed; otherwise shows the friendly
 * AccessDenied fallback. Intended to be nested inside <ProtectedRoute> (which
 * handles authentication) — this component only concerns itself with permissions.
 *
 * Props:
 *   - permissions: string[]        allow if the user has ANY of these keys
 *   - requirePlatformAdmin: bool   allow only platform admins
 *   - requirePartner: bool         allow only users holding a partner_* key
 *
 * Gating strategy:
 *   - Regular permission routes fail OPEN while the payload loads (`hasAny` returns
 *     true until `ready`), so a bootstrap hiccup never blocks ordinary features.
 *   - Privileged routes (requirePlatformAdmin / requirePartner) fail CLOSED: we
 *     wait for the payload rather than reveal the console. This shows a brief
 *     loader instead of flashing the admin UI to a non-admin (or an AccessDenied
 *     to a real admin) on refresh.
 */
const PermissionRoute = ({
  permissions,
  requirePlatformAdmin = false,
  requirePartner = false,
  children,
}) => {
  const { hasAny, isPlatformAdmin, partnerPermissions, ready, error } = usePermissions();

  if (requirePlatformAdmin || requirePartner) {
    const allowed = requirePlatformAdmin ? isPlatformAdmin : partnerPermissions.length > 0;
    // Don't reveal the console until we've confirmed the user qualifies.
    if (ready) return allowed ? children : <AccessDenied />;
    // Bootstrap failed (can't confirm) → deny rather than expose.
    if (error) return <AccessDenied />;
    // Still resolving → hold on a neutral loader.
    return <LoadingScreen />;
  }

  if (permissions && permissions.length && !hasAny(permissions)) {
    return <AccessDenied />;
  }
  return children;
};

export default PermissionRoute;
